import React from 'react';
import { format, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import DayColumn, { type TimeSlot } from './DayColumn';
import type { EnhancedCourse } from '../../hooks/useTeacherSchedule';
import type { TrialLessonDTO } from '../../lib/trials';
import type { OverlapLayout } from '../../lib/scheduleLayout';

type DropTarget = { dayKey: string; hour: number; minute: number } | null;
type DropConflictStatus = 'checking' | 'ok' | 'conflict' | 'availability_override' | 'error' | null;

interface FullScheduleGridProps {
  days: Date[];
  timeSlots: TimeSlot[];
  courses: EnhancedCourse[];
  rowPx: number;
  gridMaxHeight: number;
  calcTop: (time: string) => number;
  calcHeight: (startTime: string, endTime: string) => number;
  onContextMenuSlot: (e: React.MouseEvent, day: Date, hour: number, minute: number) => void;
  onContextMenuCourse: (e: React.MouseEvent, course: EnhancedCourse) => void;
  onClickCourse: (course: EnhancedCourse) => void;
  onDragOverSlot: (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => void;
  onDropSlot: (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => void;
  isDraggingCourse: boolean;
  activeDropTarget: DropTarget;
  activeDropConflictStatus?: DropConflictStatus;
  onCourseDragStart: (e: React.DragEvent<HTMLDivElement>, course: EnhancedCourse) => void;
  onCourseDragEnd: () => void;
  onTrialDragStart?: (e: React.DragEvent<HTMLButtonElement>, trial: TrialLessonDTO) => void;
  onTrialDragEnd?: () => void;
  getContingentInfo?: (studentName: string) => { remainingUnits: number; totalUnits: number } | null;
  getContingentColor?: (remainingUnits: number, totalUnits: number) => string;
  getTrialsForDay: (day: Date, dayIndex: number) => TrialLessonDTO[];
  onContextMenuTrial: (e: React.MouseEvent, trial: TrialLessonDTO) => void;
  renderFloatingTrials: (
    day: Date,
    dayIndex: number,
    dayTrials: TrialLessonDTO[],
    dayLayout?: Map<string, OverlapLayout>,
  ) => React.ReactNode;
  /** Ordnet Kurse und Probestunden eines Tages gemeinsam an. */
  getDayLayout?: (dayIndex: number, dayTrials: TrialLessonDTO[]) => Map<string, OverlapLayout>;
  availabilitySlots?: Array<{ dayOfWeek: number; time_start: string; time_end: string }>;
  renderContextMenu: () => React.ReactNode;
  calendarRef: React.RefObject<HTMLDivElement>;
  showHeaderDates?: boolean;
}

const FullScheduleGrid: React.FC<FullScheduleGridProps> = ({
  days,
  timeSlots,
  courses,
  rowPx,
  gridMaxHeight,
  calcTop,
  calcHeight,
  onContextMenuSlot,
  onContextMenuCourse,
  onClickCourse,
  onDragOverSlot,
  onDropSlot,
  isDraggingCourse,
  activeDropTarget,
  activeDropConflictStatus = null,
  onCourseDragStart,
  onCourseDragEnd,
  onTrialDragStart,
  onTrialDragEnd,
  getContingentInfo,
  getContingentColor,
  getTrialsForDay,
  onContextMenuTrial,
  renderFloatingTrials,
  availabilitySlots,
  renderContextMenu,
  calendarRef,
  showHeaderDates = true,
  getDayLayout,
}) => {
  return (
    <div className="rounded-lg border relative" ref={calendarRef}>
      <div className="max-w-full overflow-auto" style={{ maxHeight: gridMaxHeight }}>
        <div className="min-w-[820px] xl:min-w-[980px]">
          <div className="grid grid-cols-8 border-b sticky top-0 z-20 bg-background">
            <div className="p-3 text-center text-sm font-medium text-muted-foreground"></div>
            {days.map((day) => (
              <div key={format(day, 'yyyy-MM-dd')} className="px-1 py-2 text-center text-xs font-medium md:text-sm">
                <div>
                  <span className="md:hidden">{format(day, 'EE', { locale: de })}</span>
                  <span className="hidden md:inline">{format(day, 'EEE', { locale: de })}</span>
                </div>
                {showHeaderDates ? (
                  <div
                    className={`mx-auto mt-1 w-fit rounded-full px-2 py-0.5 text-xs ${
                      isSameDay(day, new Date()) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {format(day, 'dd.MM.')}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-8">
            <div className="w-14 border-r sticky left-0 z-10 bg-background md:w-auto">
              {timeSlots.map((slot) => (
                <div
                  key={slot.time}
                  className="border-b p-1 text-center text-xs text-muted-foreground"
                  style={{ height: rowPx }}
                >
                  {slot.time}
                </div>
              ))}
            </div>

            {days.map((day, dayIndex) => {
              const dayTrials = getTrialsForDay(day, dayIndex);
              const dayLayout = getDayLayout?.(dayIndex, dayTrials);
              return (
                <DayColumn
                  key={format(day, 'yyyy-MM-dd')}
                  day={day}
                  dayIndex={dayIndex}
                  timeSlots={timeSlots}
                  courses={courses}
                  calcTop={calcTop}
                  calcHeight={calcHeight}
                  onContextMenuSlot={onContextMenuSlot}
                  onContextMenuCourse={onContextMenuCourse}
                  onClickCourse={onClickCourse}
                  onDragOverSlot={onDragOverSlot}
                  onDropSlot={onDropSlot}
                  isDraggingCourse={isDraggingCourse}
                  activeDropTarget={activeDropTarget}
                  activeDropConflictStatus={activeDropConflictStatus}
                  dayKey={format(day, 'yyyy-MM-dd')}
                  draggable
                  onCourseDragStart={onCourseDragStart}
                  onCourseDragEnd={onCourseDragEnd}
                  onTrialDragStart={onTrialDragStart}
                  onTrialDragEnd={onTrialDragEnd}
                  getContingentInfo={getContingentInfo}
                  getContingentColor={getContingentColor}
                  trials={dayTrials}
                  onContextMenuTrial={onContextMenuTrial}
                  rowHeight={rowPx}
                  availabilitySlots={availabilitySlots}
                  dayLayout={dayLayout}
                >
                  {renderFloatingTrials(day, dayIndex, dayTrials, dayLayout)}
                </DayColumn>
              );
            })}
          </div>
        </div>
      </div>
      {renderContextMenu()}
    </div>
  );
};

export default FullScheduleGrid;
